import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 text-gray-800">
      <h1 className="text-xl font-bold mb-6">Privacy Policy for Repotal</h1>
      <p className="mb-6">
        Repotal ("we," "our," or "us") provides a Software-as-a-Service (SaaS)
        platform to manage and streamline the sales operations of businesses.
        This Privacy Policy outlines how we collect, use, disclose, and protect
        your personal information when you use our services through the Repotal
        platform.
      </p>

      <Section title="1. Introduction">
        We are committed to safeguarding the privacy of our users. This Privacy
        Policy explains what personal data we collect, how we use it, and your
        rights regarding that data. By using Repotal, you agree to the
        collection and use of your information in accordance with this policy
      </Section>

      <Section title="2. Information We Collect">
        <p>We collect the following categories of information:</p>
        <SubSection
          title="a. Personal Information"
          listItems={[
            "Full name, email address, phone number, designation, and business details",
            "Profile information including photo, designation, department, and permissions",
          ]}
        />
        <SubSection
          title="b. Account and Authentication Data"
          listItems={[
            "Username and encrypted passwords",
            "Login timestamps and device/IP details",
          ]}
        />
        <SubSection
          title="c. Communication Data"
          listItems={[
            "Messages sent via Repotal's internal chat feature",
            "Email and push notification logs",
          ]}
        />
        <SubSection
          title="d. Usage and Activity Data"
          listItems={[
            "Lead assignments and updates",
            "Visit logs and meeting outcomes",
            "Task completions and time stamps",
            "System-generated notifications and interactions",
          ]}
        />
        <SubSection
          title="e. Location Information"
          listItems={[
            "Location tracking when enabled, including GPS coordinates and travel routes",
            "Visit start/end locations, travel deviations, and timestamped movement",
          ]}
        />
        <SubSection
          title="f. Billing and Payment Information"
          listItems={[
            "Cardholder name, masked card numbers, billing address",
            "Transaction history, subscription plans, and renewal dates (via third-party processor)",
          ]}
        />
        <SubSection
          title="g. Support Interactions"
          listItems={[
            "Helpdesk ticket data, support agent interactions, and feedback",
          ]}
        />
      </Section>

      <Section title="3. How We Use the Information">
        <ul className="list-disc list-inside space-y-1">
          <li>Create and manage your Repotal account</li>
          <li>
            Facilitate lead assignments, meeting scheduling, visit tracking, and
            task reminders
          </li>
          <li>
            Send notifications for upcoming activities, expirations, or required
            actions
          </li>
          <li>Provide support and respond to inquiries or issues</li>
          <li>Manage billing, renewals, and subscription services</li>
          <li>Improve and personalize the user experience</li>
          <li>
            Monitor usage for performance optimization and fraud prevention
          </li>
        </ul>
      </Section>

      <Section title="4. Sharing and Disclosure of Information">
        <SubSection title="a. Within Your Organization">
          Depending on your role (e.g., Sales Executive, Company Admin, Super
          Admin), your data may be visible to others within your organization
          for operational purposes.
        </SubSection>
        <SubSection title="b. Service Providers">
          We share data with trusted vendors for payment processing, cloud
          hosting, analytics, communication (email/SMS/push), and support ticket
          systems.
        </SubSection>
        <SubSection title="c. Legal and Regulatory Disclosure">
          We may disclose your data if required to do so by law, subpoena, or in
          response to valid legal requests by authorities.
        </SubSection>
      </Section>

      <Section title="5. Cookies and Tracking Technologies">
        <p className="mb-2">We use cookies and similar technologies to:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Maintain user sessions and authentication</li>
          <li>Remember user preferences</li>
          <li>Analyze user activity for product improvement</li>
          <li>Detect security anomalies and prevent fraud</li>
        </ul>
        <p className="mt-2">
          You can control cookie settings through your browser. However,
          disabling cookies may impact some functionalities of Repotal.
        </p>
      </Section>

      <Section title="6. Data Security">
        <ul className="list-disc list-inside space-y-1">
          <li>Encrypted communications via HTTPS</li>
          <li>Secure password storage</li>
          <li>Firewalls, intrusion detection, and access logs</li>
          <li>Role-based access controls</li>
        </ul>
      </Section>

      <Section title="7. Data Retention">
        <p>We retain personal data:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>As long as your account remains active</li>
          <li>
            As necessary to comply with legal obligations and enforce our
            agreements
          </li>
          <li>
            With periodic reviews to anonymize or delete inactive accounts after
            a retention threshold
          </li>
        </ul>
      </Section>

      <Section title="8. Your Rights and Choices">
        <ul className="list-disc list-inside space-y-1">
          <li>Accessing your data</li>
          <li>Correcting inaccurate information</li>
          <li>Deleting your account or requesting erasure of data</li>
          <li>Objecting to or restricting certain processing activities</li>
          <li>Withdrawing consent (where applicable)</li>
        </ul>
        <p className="mt-2">
          To exercise any of these rights, please contact{" "}
          <a
            href="mailto:support@repotal.com"
            className="text-blue-600 underline"
          >
            support@repotal.com
          </a>
          . Verification may be required.
        </p>
      </Section>

      <Section title="9. International Data Transfers">
        Our servers and service providers may be located outside your country of
        residence. By using Repotal, you consent to the transfer of your
        information to countries that may have different data protection rules.
      </Section>

      <Section title="10. Children’s Privacy">
        Repotal is intended for users aged 18 and above. We do not knowingly
        collect personal data from individuals under 18.
      </Section>

      <Section title="11. Updates to This Policy">
        We may revise this Privacy Policy periodically. We will notify you of
        material changes through email or in-app alerts. Continued use of
        Repotal constitutes acceptance of updated policies.
      </Section>

      <Section title="12. Contact Us">
        For questions, feedback, or data requests: Email{": "}
        <a
          href="mailto:support@repotal.com"
          className="text-blue-600 underline"
        >
          support@repotal.com
        </a>
      </Section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    <div className="text-gray-700 space-y-2">{children}</div>
  </div>
);

const SubSection = ({ title, children, listItems }) => (
  <div className="mb-4">
    <h3 className="text-lg font-medium">{title}</h3>
    {children && <p>{children}</p>}
    {listItems && (
      <ul className="list-disc list-inside space-y-1 mt-1">
        {listItems.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    )}
  </div>
);

export default PrivacyPolicy;
